import { useState } from 'react'
import { router } from '@inertiajs/react'
import { AlertTriangle, Bot, Building2, Check, ChevronDown, History, RefreshCw } from 'lucide-react'
import type { BroadcastFailure, HistoryEntry } from './types'

interface Props {
  history: HistoryEntry[]
}

function failedTotalOf(entry: HistoryEntry): number {
  return entry.sessionsFailed + entry.panelFailed + entry.emailFailed
}

function StatusChip({ entry }: { entry: HistoryEntry }) {
  const base = 'rounded-full border px-2 py-px text-[10px] font-bold whitespace-nowrap'
  const failedTotal = failedTotalOf(entry)
  if (entry.status === 'sending') {
    return <span className={`${base} border-blue-400/35 bg-blue-400/10 text-blue-400`}>Enviando</span>
  }
  if (failedTotal > 0) {
    return (
      <span className={`${base} border-amber-500/35 bg-amber-500/10 text-amber-500`}>
        {failedTotal} {failedTotal === 1 ? 'falha' : 'falhas'}
      </span>
    )
  }
  return <span className={`${base} border-emerald-500/35 bg-emerald-500/10 text-emerald-500`}>Enviado</span>
}

function DeliveryStat({ ok, delivered, total, unit }: { ok: boolean; delivered: number; total: number; unit: string }) {
  const Icon = ok ? Check : AlertTriangle
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-500' : 'text-amber-500'}`}>
      <Icon className="h-3 w-3" />
      {delivered}/{total} {unit}
    </span>
  )
}

function failureUnit(failure: BroadcastFailure): string {
  if (failure.channel === 'session') return failure.count === 1 ? 'sessão' : 'sessões'
  if (failure.channel === 'email') return failure.count === 1 ? 'e-mail' : 'e-mails'
  return failure.count === 1 ? 'notificação' : 'notificações'
}

function failureTarget(failure: BroadcastFailure): string {
  // Falhas de sessão apontam o agente; painel/e-mail apontam o grupo de usuários.
  if (failure.channel === 'session' && failure.workerName) {
    return `${failure.orgName} — ${failure.workerName}`
  }
  return `${failure.orgName} — ${failure.count} ${failure.count === 1 ? 'usuário' : 'usuários'}`
}

function ImpactPart({ count, singular, plural }: { count: number; singular: string; plural: string }) {
  if (count === 0) return null
  return (
    <strong className="font-semibold text-foreground">
      {count} {count === 1 ? singular : plural}
    </strong>
  )
}

function FailureDetail({ entry, retrying, onRetry }: { entry: HistoryEntry; retrying: boolean; onRetry: () => void }) {
  // Os totais vêm da contagem exata do backend; `failures` são grupos de
  // detalhe e podem estar truncados em falhas muito espalhadas.
  const failedTotal = failedTotalOf(entry)
  const groupedCount = entry.failures.reduce((sum, failure) => sum + failure.count, 0)
  const retryBusy = retrying || entry.status === 'sending'
  const impactParts = [
    { count: entry.sessionsFailed, singular: 'sessão de agente', plural: 'sessões de agente' },
    { count: entry.panelFailed, singular: 'notificação no painel', plural: 'notificações no painel' },
    { count: entry.emailFailed, singular: 'e-mail', plural: 'e-mails' },
  ].filter((part) => part.count > 0)

  return (
    <div className="mt-2 rounded-md border border-amber-500/35 bg-amber-500/5 px-3 py-2.5">
      {entry.failures.map((failure, index) => (
        <div key={index} className="flex gap-2 border-b border-dashed border-border/40 py-1.5 text-xs last:border-b-0">
          <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0 text-amber-500" />
          <div className="min-w-0">
            <span className="font-semibold text-foreground">{failureTarget(failure)}</span> ·{' '}
            <span className="whitespace-nowrap font-semibold text-amber-500">
              {failure.count} {failureUnit(failure)}
            </span>
            <p className="text-muted-foreground">{failure.reason}</p>
          </div>
        </div>
      ))}
      {groupedCount < failedTotal && (
        <p className="mt-1.5 text-[11px] text-muted-foreground">
          … e mais {failedTotal - groupedCount} {failedTotal - groupedCount === 1 ? 'falha' : 'falhas'} em outros
          grupos (o reenvio cobre todas).
        </p>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">
        Impacto:{' '}
        {impactParts.map((part, index) => (
          <span key={part.singular}>
            {index > 0 && (index === impactParts.length - 1 ? ' e ' : ', ')}
            <ImpactPart count={part.count} singular={part.singular} plural={part.plural} />
          </span>
        ))}{' '}
        sem o comunicado até o reenvio.
      </p>
      <button
        type="button"
        disabled={retryBusy}
        onClick={onRetry}
        className="mt-2.5 inline-flex items-center gap-1.5 rounded-md border border-amber-500/35 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-500 transition-colors hover:bg-amber-500/20 disabled:cursor-default disabled:opacity-65 disabled:hover:bg-amber-500/10"
      >
        <RefreshCw className={`h-3 w-3 ${retryBusy ? 'animate-spin' : ''}`} />
        {retryBusy ? 'Reenviando falhas...' : `Reenviar ${failedTotal} ${failedTotal === 1 ? 'falha' : 'falhas'}`}
      </button>
    </div>
  )
}

interface ItemProps {
  entry: HistoryEntry
  open: boolean
  retrying: boolean
  onToggle: () => void
  onRetry: () => void
}

function HistoryItem({ entry, open, retrying, onToggle, onRetry }: ItemProps) {
  const sessionsOk = entry.sessionsFailed === 0 && entry.sessionsDelivered >= entry.sessionsTotal
  const panelOk = entry.panelFailed === 0 && entry.panelDelivered >= entry.panelTotal
  const emailOk = entry.emailFailed === 0 && entry.emailDelivered >= entry.emailTotal
  const failedTotal = failedTotalOf(entry)
  const showFailures = entry.failures.length > 0 && entry.status !== 'sending'
  const hasUserChannels = entry.panelTotal > 0 || entry.emailTotal > 0

  return (
    <div className="border-b border-border/40 py-3 first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground" title={entry.title}>
          {entry.title}
        </p>
        <StatusChip entry={entry} />
      </div>
      <div className="mt-1 flex flex-wrap gap-2.5 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <History className="h-3 w-3" />
          {entry.createdAtLabel}
        </span>
        <span className="inline-flex items-center gap-1">
          <Building2 className="h-3 w-3" />
          {entry.orgsCount} {entry.orgsCount === 1 ? 'org' : 'orgs'}
        </span>
        <span className="inline-flex items-center gap-1">
          <Bot className="h-3 w-3" />
          {entry.agentsCount} {entry.agentsCount === 1 ? 'agente' : 'agentes'}
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-3">
        {/* Comunicados só para usuários (sem agentes) não mostram a linha vazia de sessões. */}
        {(entry.sessionsTotal > 0 || !hasUserChannels) && (
          <DeliveryStat
            ok={sessionsOk}
            delivered={entry.sessionsDelivered}
            total={entry.sessionsTotal}
            unit="sessões"
          />
        )}
        {entry.panelTotal > 0 && (
          <DeliveryStat ok={panelOk} delivered={entry.panelDelivered} total={entry.panelTotal} unit="painel" />
        )}
        {entry.emailTotal > 0 && (
          <DeliveryStat ok={emailOk} delivered={entry.emailDelivered} total={entry.emailTotal} unit="e-mails" />
        )}
      </div>
      {showFailures && (
        <>
          <button
            type="button"
            onClick={onToggle}
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-500 hover:underline"
          >
            <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
            {open ? 'Ocultar falhas' : `Ver ${failedTotal} ${failedTotal === 1 ? 'falha' : 'falhas'} — por quê`}
          </button>
          {open && <FailureDetail entry={entry} retrying={retrying} onRetry={onRetry} />}
        </>
      )}
    </div>
  )
}

export default function HistoryCard({ history }: Props) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [retrying, setRetrying] = useState<Record<string, boolean>>({})

  const toggleEntry = (id: string) => {
    setExpanded((previous) => ({ ...previous, [id]: !previous[id] }))
  }

  const retryEntry = (id: string) => {
    setRetrying((previous) => ({ ...previous, [id]: true }))
    router.post(
      `/cc/broadcasts/${id}/retry`,
      {},
      {
        preserveScroll: true,
        // Após o redirect a entrada volta a 'sending' e o polling do Index assume;
        // onFinish só limpa a flag local (inclusive quando o reenvio falha).
        onFinish: () => setRetrying((previous) => ({ ...previous, [id]: false })),
      }
    )
  }

  return (
    <section className="rounded-xl border border-border bg-card shadow-sm">
      <div className="px-5 pt-4">
        <h3 className="flex items-center gap-2 text-[15px] font-semibold text-foreground">
          <History className="h-4 w-4 text-muted-foreground" />
          Histórico
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">Últimos comunicados enviados</p>
      </div>
      <div className="p-5 pt-3">
        {history.map((entry) => (
          <HistoryItem
            key={entry.id}
            entry={entry}
            open={Boolean(expanded[entry.id])}
            retrying={Boolean(retrying[entry.id])}
            onToggle={() => toggleEntry(entry.id)}
            onRetry={() => retryEntry(entry.id)}
          />
        ))}
        {history.length === 0 && (
          <p className="py-1 text-sm text-muted-foreground">Nenhum comunicado enviado ainda.</p>
        )}
      </div>
    </section>
  )
}
